import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Box, Button, Chip, CircularProgress, Link, Typography } from '@material-ui/core'
import axios from 'axios'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider,
} from '../v3/maricopaBasemap'

import DrawZoneModal from '../v3/DrawZoneModal'

const SPRAY_TYPE_COLORS = {
  turf: { fill: '#00FF00', stroke: '#00AA00' },
  ornamental: { fill: '#FFFF00', stroke: '#CCAA00' },
  bare_ground: { fill: '#FFA500', stroke: '#CC7700' },
  none: { fill: '#9e9e9e', stroke: '#777777' }
}

const SPRAY_TYPE_LABELS = {
  turf: 'Turf',
  ornamental: 'Ornamental',
  bare_ground: 'Bare Ground',
  none: 'Other',
  other: 'Other'
}

const normalizePoint = (point = {}) => {
  const lat = Number(point.lat ?? point.latitude)
  const lng = Number(point.lng ?? point.lon ?? point.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const normalizePaths = (paths = []) => paths
  .map(normalizePoint)
  .filter(Boolean)

const SprayScopeSection = ({ url, scheduleItemId, jobsiteId, sprayScope, onScopeChange }) => {
  const [areas, setAreas] = useState([])
  const [propertyLines, setPropertyLines] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [drawModalOpen, setDrawModalOpen] = useState(false)
  const [mapContainerNode, setMapContainerNode] = useState(null)

  // Leaflet refs
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const tileLayerRef = useRef(null)
  const polygonLayersRef = useRef([])

  // Local selection state
  const [mode, setMode] = useState(sprayScope?.mode || 'all')
  const [selectedIds, setSelectedIds] = useState(new Set(sprayScope?.selectedAreaIds || []))
  const [customZones, setCustomZones] = useState(sprayScope?.customZones || [])
  const [dirty, setDirty] = useState(false)

  // Sync local state when sprayScope prop changes (e.g., different item selected)
  useEffect(() => {
    setMode(sprayScope?.mode || 'all')
    setSelectedIds(new Set((sprayScope?.selectedAreaIds || []).map(String)))
    setCustomZones(sprayScope?.customZones || [])
    setDirty(false)
  }, [scheduleItemId, sprayScope])

  const fetchAreas = useCallback(async () => {
    if (!scheduleItemId) return
    setLoading(true)
    try {
      const params = jobsiteId ? { jobsiteId } : {}
      const resp = await axios.get(`${url}/api/herbicide-schedule/${scheduleItemId}/areas`, { params })
      const fetched = resp.data.areas || []
      setAreas(fetched)
      setPropertyLines(resp.data.propertyLines || [])
    } catch (err) {
      console.error('Error fetching areas:', err)
    }
    setLoading(false)
  }, [url, scheduleItemId, jobsiteId])

  useEffect(() => { fetchAreas() }, [fetchAreas])

  // Determine if an area is selected
  const isSelected = useCallback((areaId) => {
    if (mode === 'all') return true
    return selectedIds.has(String(areaId))
  }, [mode, selectedIds])

  const handleMapContainerRef = useCallback((node) => {
    mapContainerRef.current = node
    setMapContainerNode(node)
  }, [])

  // Initialize Leaflet map once the container exists after loading
  useEffect(() => {
    if (loading || !mapContainerNode) return

    // Clean up previous map if exists
    if (mapInstanceRef.current) {
      try { mapInstanceRef.current.remove() } catch (_) {}
      mapInstanceRef.current = null
    }

    const map = L.map(mapContainerNode, {
      center: [33.5, -112.15],
      zoom: 17,
      maxZoom: 28,
      zoomControl: true,
    })

    applyBasemapProvider({
      map,
      tileLayerRef,
      provider: googleHybridProvider,
    })

    mapInstanceRef.current = map

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapInstanceRef.current) return
        applyBasemapProvider({
          map,
          tileLayerRef,
          provider,
        })
      })
      .catch((err) => {
        console.warn('Failed to load latest Maricopa basemap for spray scope:', err)
      })

    // Leaflet needs a kick after the container is laid out
    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 300)

    return () => {
      cancelled = true
      if (tileLayerRef.current) {
        try { map.removeLayer(tileLayerRef.current) } catch (_) {}
        tileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapInstanceRef.current = null
    }
  }, [scheduleItemId, loading, mapContainerNode])

  // Draw / update polygons when selection or areas change
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return

    // Clear existing polygons
    polygonLayersRef.current.forEach(layer => map.removeLayer(layer))
    polygonLayersRef.current = []

    const bounds = L.latLngBounds([])

    // Draw area polygons (show ALL areas, not just spray-typed ones)
    areas.forEach(area => {
      const normalizedPaths = normalizePaths(area.paths)
      if (normalizedPaths.length < 3) return
      const selected = isSelected(area._id)
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: selected ? '#FFEB3B' : '#9e9e9e',
        fillOpacity: selected ? 0.55 : 0.15,
        color: selected ? '#F57F17' : '#ffffff',
        weight: selected ? 3.5 : 1.5,
        opacity: selected ? 1 : 0.45,
        dashArray: selected ? null : '4,4',
      }).addTo(map)

      polygon.on('click', () => handleToggleArea(area._id))
      polygonLayersRef.current.push(polygon)
    })

    // Draw custom zones
    customZones.forEach(zone => {
      const normalizedPaths = normalizePaths(zone.paths)
      if (normalizedPaths.length < 3) return
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: '#1976d2',
        fillOpacity: 0.25,
        color: '#1565c0',
        weight: 2,
        dashArray: '6,3',
      }).addTo(map)
      polygonLayersRef.current.push(polygon)
    })

    // Draw property boundary lines
    propertyLines.forEach(line => {
      const normalizedPaths = normalizePaths(line.paths)
      if (normalizedPaths.length < 3) return
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polyline = L.polygon(latlngs, {
        fill: false,
        color: '#ffffff',
        weight: 3,
        opacity: 0.85,
        dashArray: '8,6',
      }).addTo(map)
      polygonLayersRef.current.push(polyline)
    })

    // Fit bounds if we have any
    if (bounds.isValid()) {
      map.invalidateSize()
      map.fitBounds(bounds, { padding: [20, 20] })
    }
  }, [areas, propertyLines, mode, selectedIds, customZones, isSelected, scheduleItemId, loading, mapContainerNode])

  // Compute center from areas for DrawZoneModal
  const mapCenter = useMemo(() => {
    const allPaths = areas.flatMap(a => normalizePaths(a.paths))
    if (!allPaths.length) return null
    return {
      lat: allPaths.reduce((s, p) => s + p.lat, 0) / allPaths.length,
      lng: allPaths.reduce((s, p) => s + p.lng, 0) / allPaths.length,
    }
  }, [areas])

  // Group areas by spray type for chips (include all types)
  const areasByType = useMemo(() => {
    const groups = {}
    for (const area of areas) {
      const type = area.sprayType || 'other'
      if (!groups[type]) groups[type] = []
      groups[type].push(area)
    }
    return groups
  }, [areas])

  // Calculate acreage
  const computeAcres = useCallback((areaList, selectedSet, currentMode) => {
    let total = 0
    for (const area of areaList) {
      if (currentMode === 'all' || selectedSet.has(String(area._id))) {
        total += area.acres || parseFloat(area.areaAcres) || (parseFloat(area.area) ? parseFloat(area.area) / 43560 : 0)
      }
    }
    for (const zone of customZones) {
      total += zone.areaAcres || 0
    }
    return Math.round(total * 100) / 100
  }, [customZones])

  const totalAcres = computeAcres(areas, selectedIds, mode)

  const selectedCount = mode === 'all'
    ? areas.length
    : areas.filter(a => selectedIds.has(String(a._id))).length

  const sprayableCount = areas.length

  // Chip handlers
  const handleSelectAll = () => {
    setMode('all')
    setSelectedIds(new Set())
    setDirty(true)
  }

  const handleSelectNone = () => {
    setMode('selected')
    setSelectedIds(new Set())
    setDirty(true)
  }

  const handleSelectByType = (sprayType) => {
    const typeAreaIds = (areasByType[sprayType] || []).map(a => String(a._id))
    const newSelected = new Set(selectedIds)

    // Toggle: if all of this type are selected, deselect them; otherwise select them
    const allSelected = typeAreaIds.every(id => mode === 'all' || newSelected.has(id))
    if (allSelected && mode !== 'all') {
      typeAreaIds.forEach(id => newSelected.delete(id))
    } else {
      // If switching from 'all' mode, start with all areas selected then toggle
      if (mode === 'all') {
        areas.forEach(a => newSelected.add(String(a._id)))
      }
      typeAreaIds.forEach(id => newSelected.add(id))
    }

    // Check if all sprayable areas are now selected
    const allSprayable = areas
    if (allSprayable.every(a => newSelected.has(String(a._id))) && customZones.length === 0) {
      setMode('all')
      setSelectedIds(new Set())
    } else {
      setMode('selected')
      setSelectedIds(newSelected)
    }
    setDirty(true)
  }

  const handleToggleArea = (areaId) => {
    const id = String(areaId)
    const newSelected = new Set(mode === 'all'
      ? areas.map(a => String(a._id))
      : selectedIds
    )

    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }

    // Auto-detect "all selected"
    const allSprayable = areas
    if (allSprayable.every(a => newSelected.has(String(a._id))) && customZones.length === 0) {
      setMode('all')
      setSelectedIds(new Set())
    } else {
      setMode('selected')
      setSelectedIds(newSelected)
    }
    setDirty(true)
  }

  const handleAddCustomZone = (zone) => {
    setCustomZones(prev => [...prev, zone])
    if (mode === 'all') {
      // Keep all areas selected, just add the custom zone
    }
    setDirty(true)
  }

  const handleRemoveCustomZone = (index) => {
    setCustomZones(prev => prev.filter((_, i) => i !== index))
    setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const body = {
        mode,
        selectedAreaIds: mode === 'all' ? [] : [...selectedIds],
        customZones,
        totalAcres
      }
      await axios.put(`${url}/api/herbicide-schedule/${scheduleItemId}/spray-scope`, body)
      setDirty(false)
      if (onScopeChange) onScopeChange(body)
    } catch (err) {
      console.error('Error saving spray scope:', err)
    }
    setSaving(false)
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
  }

  if (!areas.length && !customZones.length) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
          No areas mapped for this site.
          {jobsiteId && (
            <> <Link href={`/site-takeoff-v3/${jobsiteId}`} target="_blank">Open SiteTakeoff to add areas</Link></>
          )}
        </Typography>
      </Box>
    )
  }

  return (
    <div>
      {/* Chip bar */}
      <Box display="flex" style={{ gap: 4, padding: '6px 0 6px 0', flexWrap: 'wrap' }} alignItems="center">
        <Chip
          size="small"
          label={mode === 'all' ? '✓ All' : 'All'}
          onClick={handleSelectAll}
          style={{
            backgroundColor: mode === 'all' ? '#4caf50' : '#fff',
            color: mode === 'all' ? '#fff' : '#666',
            border: mode === 'all' ? 'none' : '1px solid #ddd',
            fontWeight: mode === 'all' ? 600 : 400,
            cursor: 'pointer'
          }}
        />
        {Object.entries(areasByType).map(([type, typeAreas]) => {
          const typeIds = typeAreas.map(a => String(a._id))
          const allOfTypeSelected = mode === 'all' || typeIds.every(id => selectedIds.has(id))
          return (
            <Chip
              key={type}
              size="small"
              label={`${allOfTypeSelected ? '✓ ' : ''}${SPRAY_TYPE_LABELS[type] || type} (${typeAreas.length})`}
              onClick={() => handleSelectByType(type)}
              style={{
                backgroundColor: allOfTypeSelected ? `${SPRAY_TYPE_COLORS[type]?.fill}30` : '#fff',
                color: SPRAY_TYPE_COLORS[type]?.stroke || '#666',
                border: `1px solid ${SPRAY_TYPE_COLORS[type]?.stroke || '#ddd'}40`,
                fontWeight: allOfTypeSelected ? 600 : 400,
                cursor: 'pointer'
              }}
            />
          )
        })}
        <Chip
          size="small"
          label="None"
          onClick={handleSelectNone}
          style={{ backgroundColor: '#f5f5f5', color: '#999', border: '1px solid #e0e0e0', cursor: 'pointer' }}
        />
        <Box flex={1} />
        {customZones.length > 0 && (
          <Chip
            size="small"
            label={`${customZones.length} Custom Zone${customZones.length > 1 ? 's' : ''}`}
            style={{ backgroundColor: '#1565c0', color: '#fff', fontWeight: 600 }}
          />
        )}
        <Chip
          size="small"
          label={customZones.length > 0 ? '✏ Draw Another' : '✏ Draw Custom'}
          onClick={() => setDrawModalOpen(true)}
          style={{ backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', cursor: 'pointer' }}
        />
      </Box>

      {/* Interactive map */}
      <div ref={handleMapContainerRef} style={{ width: '100%', height: 350, borderRadius: 6 }} />

      {/* Summary + Save */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '6px 0' }}>
        <div>
          <Typography variant="caption" style={{ color: '#2e7d32' }}>
            {mode === 'all' ? `All ${sprayableCount} areas` : `${selectedCount} of ${sprayableCount} areas`}
            {customZones.length > 0 && ` + ${customZones.length} custom`}
            {' · '}{totalAcres} ac
          </Typography>
          {dirty && <Typography variant="caption" style={{ color: '#ff9800', marginLeft: 8 }}>unsaved</Typography>}
        </div>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{ fontSize: 11 }}
        >
          {saving ? 'Saving...' : 'Save Scope'}
        </Button>
      </Box>

      {/* Custom zone management */}
      {customZones.length > 0 && (
        <Box style={{ paddingBottom: 4 }}>
          {customZones.map((zone, idx) => (
            <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '2px 0' }}>
              <Typography variant="caption" style={{ color: '#1565c0' }}>
                {zone.label || `Custom Zone ${idx + 1}`} — {zone.areaAcres} ac
              </Typography>
              <Typography
                variant="caption"
                style={{ color: '#c62828', cursor: 'pointer' }}
                onClick={() => handleRemoveCustomZone(idx)}
              >
                Remove ×
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Links */}
      <Box style={{ paddingTop: 2 }}>
        {jobsiteId && (
          <Link href={`/site-takeoff-v2/${jobsiteId}`} target="_blank" style={{ fontSize: 10, color: '#1976d2' }}>
            Edit base areas in SiteTakeoff →
          </Link>
        )}
      </Box>

      {/* Draw Zone Modal */}
      {drawModalOpen && (
        <DrawZoneModal
          open={drawModalOpen}
          onClose={() => setDrawModalOpen(false)}
          onAddZone={handleAddCustomZone}
          existingAreas={areas}
          center={mapCenter}
        />
      )}
    </div>
  )
}

export default SprayScopeSection
